import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { NS } from "./U2104";
import { doFindCountries, doFindStates, luHandleAppiError } from "./U2104Service";

const CRITERIA_TYPE = [] as { fieldName: string; value: unknown }[];
export type U2104FindSearchCriterias = typeof CRITERIA_TYPE;

/** Handler hook pour findCountries (BL-0049). */
export function useU2104FindCountriesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U2104FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindCountries(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour findStates (BL-0049, filtre countryId côté composant). */
export function useU2104FindStatesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U2104FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindStates(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}
